import { translateLabel } from "@/i18n/messages";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CalendarBlankIcon, ArrowClockwiseIcon, ArrowLeftIcon, MountainsIcon } from "@phosphor-icons/react";
import { Navbar } from "@/components/Navbar";
import { MapView } from "@/components/MapView";
import { EventCard } from "@/components/EventCard";
import { EventDetails } from "@/components/EventDetails";
import { getUpcomingRideEvents } from "@/lib/eventsApi";
import { api } from "@/lib/apiClient";
import { parkPath } from "@/lib/routes";

// Namespace IDs so a destination and an event can never collide.
const eventPinId = (id) => `ride-event:${id}`;
const parkPinId = (id) => `destination:${id}`;

/** Shared-map preview: both entity types use one MapView and one viewport.
 * The final integration still needs the existing Discover search/filter sidebar. */
export default function Events() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [events, setEvents] = useState([]);
  const [parks, setParks] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [retry, setRetry] = useState(0);
  const [mobileView, setMobileView] = useState("map");
  const [flyToCenter, setFlyToCenter] = useState(null);
  const [category, setCategory] = useState("all");
  const [showParks, setShowParks] = useState(true);
  const [showEvents, setShowEvents] = useState(true);
  const listRef = useRef(null);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError("");
    Promise.allSettled([
      getUpcomingRideEvents({ signal: controller.signal }),
      api.get("/parks", { params: {} }),
    ]).then(([eventResult, parkResult]) => {
      if (controller.signal.aborted) return;
      if (eventResult.status === "fulfilled") {
        setEvents(eventResult.value.filter((item) => Number.isFinite(item.latitude) && Number.isFinite(item.longitude)));
      } else {
        setEvents([]);
        setError(eventResult.reason?.message || translateLabel("Events are unavailable"));
      }
      if (parkResult.status === "fulfilled") {
        setParks((parkResult.value.data?.parks || []).filter((item) => Number.isFinite(item.lat) && Number.isFinite(item.lng)));
      } else {
        setParks([]);
      }
    }).finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
  }, [retry]);

  const categories = useMemo(() => [...new Set(events.map((item) => item.category))].sort(), [events]);
  const visibleEvents = useMemo(() => showEvents ? events.filter((item) => category === "all" || item.category === category) : [], [events, category, showEvents]);
  const selectedEvent = visibleEvents.find((item) => eventPinId(item.id) === selectedId) || null;
  const selectedPark = showParks ? parks.find((item) => parkPinId(item.id) === selectedId) : null;

  useEffect(() => {
    if (loading || events.length === 0 || selectedId) return;
    const requestedId = searchParams.get("event");
    if (!requestedId) return;
    const requested = events.find((item) => item.id === requestedId);
    if (!requested) return;
    setShowEvents(true);
    setCategory("all");
    setSelectedId(eventPinId(requested.id));
    setFlyToCenter([requested.latitude, requested.longitude]);
    setMobileView("map");
  }, [events, loading, searchParams, selectedId]);

  const clearSelectedEvent = useCallback(() => {
    setSelectedId(null);
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      next.delete("event");
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  const pins = useMemo(() => [
    ...(showParks ? parks.map((item) => ({ ...item, id: parkPinId(item.id) })) : []),
    ...visibleEvents.map((item) => ({ id: eventPinId(item.id), name: item.title, lat: item.latitude, lng: item.longitude })),
  ], [parks, visibleEvents, showParks]);

  const selectEvent = useCallback((event) => {
    setShowEvents(true);
    setCategory("all"); // A stop selected from a series must remain visible.
    setSelectedId(eventPinId(event.id));
    setFlyToCenter([event.latitude, event.longitude]);
    setMobileView("map");
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      next.set("event", event.id);
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  const selectPin = useCallback((id) => {
    setSelectedId(id);
    const event = visibleEvents.find((item) => eventPinId(item.id) === id);
    if (event) {
      selectEvent(event);
      return;
    }
    const park = parks.find((item) => parkPinId(item.id) === id);
    if (park) {
      setFlyToCenter([park.lat, park.lng]);
      setMobileView("map");
    }
  }, [parks, visibleEvents, selectEvent]);

  useEffect(() => {
    if (!selectedId) return;
    const row = [...(listRef.current?.querySelectorAll("[data-event-id]") || [])]
      .find((element) => element.getAttribute("data-event-id") === selectedId);
    row?.scrollIntoView({ block: "nearest", behavior: "smooth" });
  }, [selectedId, visibleEvents]);

  const toggleParks = () => {
    setShowParks((previous) => !previous);
    if (selectedId?.startsWith("destination:")) setSelectedId(null);
  };
  const toggleEvents = () => {
    setShowEvents((previous) => !previous);
    if (selectedId?.startsWith("ride-event:")) clearSelectedEvent();
  };

  return (
    <main className="relative h-[100dvh] overflow-hidden bg-[#09090b] text-white">
      <Navbar />
      <div className="absolute inset-0 z-0">
        <MapView parks={pins} cluster selectedId={selectedId} onSelect={selectPin} flyToCenter={flyToCenter} />
      </div>
      <aside aria-label={translateLabel("Riding spots and events")} className={`absolute bottom-0 left-0 top-16 z-[1100] w-full p-3 md:top-20 md:w-[27rem] ${mobileView === "map" ? "hidden md:block" : "block"}`}>
        <div className="flex h-full flex-col overflow-hidden rounded-2xl border border-white/10 bg-black/85 backdrop-blur-xl">
          <div className="space-y-3 border-b border-white/10 p-4">
            <div className="flex items-center justify-between gap-2">
              <h1 className="flex items-center gap-2 text-lg font-bold"><CalendarBlankIcon className="text-lime-400" /> {translateLabel("Discover rides")}</h1>
              <button type="button" onClick={() => setRetry((value) => value + 1)} aria-label={translateLabel("Refresh map results")} className="rounded-lg p-2 hover:bg-white/10"><ArrowClockwiseIcon size={18} /></button>
            </div>
            <Link to="/" className="inline-flex items-center gap-1 text-xs text-zinc-300 hover:text-lime-400"><ArrowLeftIcon size={14} /> {translateLabel("Main Discover")}</Link>
            <div className="flex gap-2" aria-label={translateLabel("Map layers")}>
              <button type="button" onClick={toggleParks} aria-pressed={showParks} className={`rounded-full border px-3 py-1.5 text-xs font-semibold ${showParks ? "border-lime-400 bg-lime-400/15 text-lime-400" : "border-white/20 text-zinc-400"}`}>{translateLabel("Bike parks")}</button>
              <button type="button" onClick={toggleEvents} aria-pressed={showEvents} className={`rounded-full border px-3 py-1.5 text-xs font-semibold ${showEvents ? "border-lime-400 bg-lime-400/15 text-lime-400" : "border-white/20 text-zinc-400"}`}>{translateLabel("Events")}</button>
            </div>
            {showEvents && <><label className="block text-xs text-zinc-300" htmlFor="event-category">{translateLabel("Event type")}</label>
              <select id="event-category" value={category} onChange={(e) => { setCategory(e.target.value); clearSelectedEvent(); }} className="w-full rounded-lg border border-white/20 bg-[#202124] p-2 text-sm text-white">
                <option value="all">{translateLabel("All events")}</option>
                {categories.map((item) => <option key={item} value={item}>{item}</option>)}
              </select></>}
            <p aria-live="polite" className="text-xs text-zinc-400">{loading ? translateLabel("Loading map…") : translateLabel("{spots} riding spots · {events} upcoming events", { spots: showParks ? parks.length : 0, events: visibleEvents.length })}</p>
          </div>
          <div ref={listRef} className="min-h-0 flex-1 space-y-2 overflow-y-auto p-3">
            {error && showEvents && <div role="alert" className="rounded-lg border border-red-500/40 p-3 text-sm text-red-300">{error} <button type="button" onClick={() => setRetry((value) => value + 1)} className="ml-2 underline">{translateLabel("Retry")}</button></div>}
            {showParks && parks.map((park) => <button key={parkPinId(park.id)} type="button" onClick={() => selectPin(parkPinId(park.id))} className={`flex w-full items-center gap-2 rounded-lg border p-3 text-left text-sm ${selectedId === parkPinId(park.id) ? "border-lime-400 bg-lime-400/10" : "border-white/10 bg-[#1b1b1b]"}`}><MountainsIcon className="shrink-0 text-lime-400" size={18} /><span className="min-w-0 flex-1 truncate">{park.name} <span className="text-zinc-400">· {park.city}</span></span><span className="text-xs text-zinc-400">{translateLabel("Park")}</span></button>)}
            {visibleEvents.map((event) => <div key={event.id} data-event-id={eventPinId(event.id)}><EventCard event={event} selected={selectedId === eventPinId(event.id)} onSelect={selectEvent} /></div>)}
            {!loading && !pins.length && <p className="p-3 text-sm text-zinc-400">{translateLabel("No results for these layers. Change the filters or try again later.")}</p>}
          </div>
        </div>
      </aside>
      {selectedEvent && <div className="absolute bottom-0 right-0 z-[1200] h-[min(78dvh,44rem)] w-full overflow-hidden rounded-t-2xl border border-white/10 bg-[#111214] shadow-2xl md:top-20 md:h-auto md:w-[25rem] md:rounded-none md:border-l">
        <EventDetails event={selectedEvent} onClose={clearSelectedEvent} onSelectEvent={selectEvent} />
      </div>}
      {selectedPark && <div className="absolute bottom-0 right-0 z-[1200] flex h-[min(55dvh,28rem)] w-full flex-col gap-3 overflow-y-auto rounded-t-2xl border border-white/10 bg-[#111214] p-5 md:top-20 md:h-auto md:w-[25rem] md:rounded-none md:border-l">
        <div className="flex items-center justify-between gap-3"><h2 className="text-lg font-bold">{selectedPark.name}</h2><button type="button" onClick={() => setSelectedId(null)} aria-label={translateLabel("Close destination details")} className="text-zinc-300">✕</button></div>
        <p className="text-sm text-zinc-400">{selectedPark.city}{selectedPark.country ? `, ${translateLabel(selectedPark.country)}` : ""}</p>
        <Link to={parkPath(selectedPark)} className="rounded-lg bg-lime-400 px-4 py-3 text-center text-sm font-semibold text-black">{translateLabel("Open destination")}</Link>
      </div>}
      {!selectedEvent && !selectedPark && <button type="button" onClick={() => setMobileView((value) => value === "map" ? "list" : "map")} className="absolute bottom-5 left-1/2 z-[1250] -translate-x-1/2 rounded-full bg-lime-400 px-5 py-3 text-sm font-bold text-black shadow-xl md:hidden">{translateLabel(mobileView === "map" ? "Show results" : "Show map")}</button>}
    </main>
  );
}
